import React, { useState, useRef } from 'react';
import styles from './FileDropZone.module.css';

const FileDropZone = ({ 
  accept = '', 
  onChange, 
  selectedFile = null, 
  errorMessage = '', 
  disabled = false,
  label = 'Drop files here or click to browse',
  helperText = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  // Surface a rejected drop instead of swallowing it — a silent console.warn
  // looked like "nothing happened" when the dropped file's extension didn't match.
  const [dropError, setDropError] = useState('');
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dragging to false if we're leaving the drop zone itself
    // Check if the related target is not a child of the current target
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type if accept is specified
      if (accept && !validateFileType(file, accept)) {
        setDropError(`"${file.name}" isn't an accepted file type (${accept}).`);
        return;
      }

      setDropError('');
      // Create a synthetic event to match the expected format
      const syntheticEvent = {
        target: {
          files: files
        }
      };
      onChange(syntheticEvent);
    }
  };

  const validateFileType = (file, acceptString) => {
    if (!acceptString) return true;

    const acceptedTypes = acceptString.split(',').map(type => type.trim());
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    return acceptedTypes.some(acceptedType => {
      if (acceptedType.startsWith('.')) {
        // Extension matching
        return fileName.endsWith(acceptedType.toLowerCase());
      } else if (acceptedType.includes('/*')) {
        // MIME type with wildcard (e.g., 'image/*')
        const baseType = acceptedType.split('/')[0];
        return fileType.startsWith(baseType + '/');
      } else {
        // Exact MIME type matching
        return fileType === acceptedType;
      }
    });
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    if (!disabled) {
      setDropError('');
      onChange(e);
    }
  };

  return (
    <div className={styles.dropZoneWrapper}>
      <div
        className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${disabled ? styles.disabled : ''} ${selectedFile ? styles.hasFile : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className={styles.fileInput}
          disabled={disabled}
        />
        
        <div className={styles.dropZoneContent}>
          {selectedFile ? (
            <>
              <div className={styles.fileIcon}>📄</div>
              <div className={styles.fileName}>{selectedFile.name}</div>
              <div className={styles.fileSize}>
                {(selectedFile.size / 1024).toFixed(2)} KB
              </div>
              <div className={styles.changeFileText}>
                Click or drag to change file
              </div>
            </>
          ) : (
            <>
              <div className={styles.uploadIcon}>📁</div>
              <div className={styles.labelText}>{label}</div>
              {helperText && <div className={styles.helperText}>{helperText}</div>}
            </>
          )}
        </div>
      </div>
      {(errorMessage || dropError) && (
        <div className={styles.errorMessage}>{errorMessage || dropError}</div>
      )}
    </div>
  );
};

export default FileDropZone;

